import { useState } from 'react';
import {
  ChevronDown,
  Headphones,
  Keyboard,
  Laptop,
  Monitor,
  Package,
  Pencil,
  Plus,
  RotateCcw,
  Smartphone,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/date';
import { usePermission } from '@/lib/permissions';
import type { EmployeeDetail } from '@/types/employee';
import type { Asset, AssetStatus } from '@hygge/shared';
import {
  useEmployeeAssets,
  useCreateAsset,
  useUpdateAsset,
  useMarkAssetReturned,
  useDeleteAsset,
} from '@/hooks/use-assets';
import { useAssetTypes } from '@/hooks/use-asset-types';
import { IconActionButton } from './icon-action-button';
import { CustomBlocksSection } from './custom-blocks/custom-blocks-section';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  laptop: Laptop,
  monitor: Monitor,
  smartphone: Smartphone,
  keyboard: Keyboard,
  headphones: Headphones,
  package: Package,
};

function getIcon(name?: string | null) {
  return (name && ICON_MAP[name]) || Package;
}

function getStatusBadge(status: AssetStatus) {
  switch (status) {
    case 'ASSIGNED':
      return <Badge variant="success">Assigned</Badge>;
    case 'RETURNED':
      return <Badge variant="default">Returned</Badge>;
    case 'LOST':
      return <Badge variant="destructive">Lost</Badge>;
    case 'BROKEN':
      return <Badge variant="warning">Broken</Badge>;
    case 'IN_REPAIR':
      return <Badge variant="info">In Repair</Badge>;
    default:
      return <Badge variant="default">{status}</Badge>;
  }
}

interface AssetsTabProps {
  employee: EmployeeDetail;
}

export function AssetsTab({ employee }: AssetsTabProps) {
  const { data: assets, isLoading } = useEmployeeAssets(employee.id);
  const createAsset = useCreateAsset();
  const updateAsset = useUpdateAsset(employee.id);
  const markReturned = useMarkAssetReturned(employee.id);
  const deleteAsset = useDeleteAsset(employee.id);
  // The write controls follow what the assets routes require, `assets.manage`, not
  // the employee-modify right: one without the other used to see buttons that
  // answered 403, or no buttons at all. [HW-942]
  const canManage = usePermission('assets.manage');
  // Only the dialog's type picker reads the catalog; `assets.manage` is one of
  // the codes the catalog reads for. [HW-942]
  const { data: assetTypes } = useAssetTypes(false, { enabled: canManage });

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editingAssetId, setEditingAssetId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<string | null>(null);
  const [returnDialogOpen, setReturnDialogOpen] = useState<string | null>(null);
  const [returnDate, setReturnDate] = useState<Date | null>(new Date());
  const [returnedOpen, setReturnedOpen] = useState(false);

  // Form state
  const todayStr = new Date().toISOString().slice(0, 10);
  const [formAssetTypeId, setFormAssetTypeId] = useState('');
  const [formName, setFormName] = useState('');
  const [formIdentifier, setFormIdentifier] = useState('');
  const [formAssignedAt, setFormAssignedAt] = useState(todayStr);
  const [formNotes, setFormNotes] = useState('');

  const resetForm = () => {
    setEditingAssetId(null);
    setFormAssetTypeId('');
    setFormName('');
    setFormIdentifier('');
    setFormAssignedAt(new Date().toISOString().slice(0, 10));
    setFormNotes('');
  };

  const openCreateDialog = () => {
    resetForm();
    setAssignDialogOpen(true);
  };

  const openEditDialog = (asset: Asset) => {
    setEditingAssetId(asset.id);
    setFormAssetTypeId(asset.assetTypeId);
    setFormName(asset.name);
    setFormIdentifier(asset.identifier ?? '');
    setFormAssignedAt(
      asset.assignedAt ? new Date(asset.assignedAt).toISOString().slice(0, 10) : todayStr
    );
    setFormNotes(asset.notes ?? '');
    setAssignDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formAssetTypeId || !formName) return;
    const assignedAtIso = formAssignedAt ? new Date(formAssignedAt).toISOString() : undefined;
    if (editingAssetId) {
      await updateAsset.mutateAsync({
        id: editingAssetId,
        name: formName,
        identifier: formIdentifier || null,
        assignedAt: assignedAtIso,
        notes: formNotes || null,
      });
    } else {
      await createAsset.mutateAsync({
        assetTypeId: formAssetTypeId,
        employeeId: employee.id,
        name: formName,
        identifier: formIdentifier || undefined,
        assignedAt: assignedAtIso,
        notes: formNotes || undefined,
      });
    }
    setAssignDialogOpen(false);
    resetForm();
  };

  const handleReturn = async () => {
    if (!returnDialogOpen) return;
    const returnedAt = returnDate
      ? `${returnDate.getFullYear()}-${String(returnDate.getMonth() + 1).padStart(2, '0')}-${String(returnDate.getDate()).padStart(2, '0')}`
      : undefined;
    await markReturned.mutateAsync({ id: returnDialogOpen, returnedAt });
    setReturnDialogOpen(null);
    setReturnDate(new Date());
  };

  const handleDelete = async (assetId: string) => {
    await deleteAsset.mutateAsync(assetId);
    setDeleteDialogOpen(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border/50 bg-muted/30 px-5 py-3">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Assigned Assets
          </h3>
        </div>
        <div className="p-5">
          <div className="space-y-3">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        </div>
      </div>
    );
  }

  const allAssets = assets || [];
  const activeAssets = allAssets.filter((a) => a.status === 'ASSIGNED');
  const otherAssets = allAssets.filter((a) => a.status !== 'ASSIGNED');

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border/50 bg-muted/30 px-5 py-3">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Assigned Assets
          </h3>
          {canManage && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={openCreateDialog}
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              Assign Asset
            </Button>
          )}
        </div>
        <div className="p-5">
          {allAssets.length === 0 ? (
            <div className="py-12 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
                <Monitor className="h-6 w-6 text-muted-foreground/60" />
              </div>
              <h4 className="mt-4 text-sm font-medium text-foreground">No Assets Assigned</h4>
              <p className="mt-1 text-xs text-muted-foreground">
                Assign equipment and devices to this employee
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Active / Assigned assets */}
              {activeAssets.length > 0 && (
                <div className="space-y-2">
                  {activeAssets.map((asset) => {
                    const Icon = getIcon(asset.assetType?.icon);
                    return (
                      <div
                        key={asset.id}
                        className="flex items-center gap-3 rounded-lg border border-border/50 bg-muted/20 px-4 py-3"
                      >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                          <Icon className="h-4.5 w-4.5 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-foreground truncate">
                              {asset.name}
                            </span>
                            {getStatusBadge(asset.status)}
                          </div>
                          <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                            {asset.assetType?.name && (
                              <span>{asset.assetType.name}</span>
                            )}
                            {asset.identifier && (
                              <>
                                <span className="text-border">|</span>
                                <span>{asset.identifier}</span>
                              </>
                            )}
                            <span className="text-border">|</span>
                            <span>
                              Assigned{' '}
                              {formatDateTime(asset.assignedAt, {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              }) || '-'}
                            </span>
                          </div>
                          {asset.notes && (
                            <p
                              className="mt-1 truncate text-xs text-muted-foreground/80"
                              title={asset.notes}
                            >
                              {asset.notes}
                            </p>
                          )}
                        </div>
                        {canManage && (
                          <div className="flex items-center gap-1">
                            <IconActionButton
                              icon={Pencil}
                              tooltip="Edit"
                              onClick={() => openEditDialog(asset)}
                              size="sm"
                            />
                            <IconActionButton
                              icon={RotateCcw}
                              tooltip="Mark as returned"
                              onClick={() => {
                                setReturnDialogOpen(asset.id);
                                setReturnDate(new Date());
                              }}
                              size="sm"
                            />
                            <IconActionButton
                              icon={Trash2}
                              tooltip="Delete"
                              onClick={() => setDeleteDialogOpen(asset.id)}
                              variant="destructive"
                              size="sm"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Returned / Other assets */}
              {otherAssets.length > 0 && (
                <Collapsible open={returnedOpen} onOpenChange={setReturnedOpen}>
                  <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted/40 transition-colors cursor-pointer">
                    <ChevronDown
                      className={cn(
                        'h-3.5 w-3.5 transition-transform',
                        returnedOpen && 'rotate-180'
                      )}
                    />
                    Returned / Other ({otherAssets.length})
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="mt-2 space-y-2">
                      {otherAssets.map((asset) => {
                        const Icon = getIcon(asset.assetType?.icon);
                        return (
                          <div
                            key={asset.id}
                            className="flex items-center gap-3 rounded-lg border border-border/50 bg-muted/10 px-4 py-3 opacity-70"
                          >
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                              <Icon className="h-4.5 w-4.5 text-muted-foreground" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-foreground truncate">
                                  {asset.name}
                                </span>
                                {getStatusBadge(asset.status)}
                              </div>
                              <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                                {asset.assetType?.name && (
                                  <span>{asset.assetType.name}</span>
                                )}
                                {asset.identifier && (
                                  <>
                                    <span className="text-border">|</span>
                                    <span>{asset.identifier}</span>
                                  </>
                                )}
                                <span className="text-border">|</span>
                                <span>
                                  Assigned{' '}
                                  {formatDateTime(asset.assignedAt, {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                  }) || '-'}
                                </span>
                                {asset.returnedAt && (
                                  <>
                                    <span className="text-border">|</span>
                                    <span>
                                      Returned{' '}
                                      {formatDateTime(asset.returnedAt, {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric',
                                      }) || '-'}
                                    </span>
                                  </>
                                )}
                              </div>
                              {asset.notes && (
                                <p
                                  className="mt-1 truncate text-xs text-muted-foreground/80"
                                  title={asset.notes}
                                >
                                  {asset.notes}
                                </p>
                              )}
                            </div>
                            {canManage && (
                              <div className="flex items-center gap-1">
                                <IconActionButton
                                  icon={Pencil}
                                  tooltip="Edit"
                                  onClick={() => openEditDialog(asset)}
                                  size="sm"
                                />
                                <IconActionButton
                                  icon={Trash2}
                                  tooltip="Delete"
                                  onClick={() => setDeleteDialogOpen(asset.id)}
                                  variant="destructive"
                                  size="sm"
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>
          )}
        </div>
      </div>

      <CustomBlocksSection section="ASSETS" employeeId={employee.id} />

      {/* Assign / Edit Asset Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setAssignDialogOpen(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingAssetId ? 'Edit Asset' : 'Assign Asset'}</DialogTitle>
            <DialogDescription>
              {editingAssetId
                ? 'Update the asset details below.'
                : `Assign a new asset to ${employee.firstName} ${employee.lastName}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="asset-type">Asset Type</Label>
              <Select
                value={formAssetTypeId}
                onValueChange={setFormAssetTypeId}
                disabled={!!editingAssetId}
              >
                <SelectTrigger id="asset-type">
                  <SelectValue placeholder="Select asset type" />
                </SelectTrigger>
                <SelectContent>
                  {assetTypes?.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="asset-name">Name</Label>
              <Input
                id="asset-name"
                placeholder="e.g. MacBook Pro 16&quot;"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="asset-identifier">Identifier</Label>
              <Input
                id="asset-identifier"
                placeholder="e.g. Serial number, asset tag"
                value={formIdentifier}
                onChange={(e) => setFormIdentifier(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="asset-assigned-at">Assign Date</Label>
              <Input
                id="asset-assigned-at"
                type="date"
                value={formAssignedAt}
                onChange={(e) => setFormAssignedAt(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="asset-notes">Notes</Label>
              <Textarea
                id="asset-notes"
                placeholder="Optional notes..."
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                rows={6}
                className="max-h-60 overflow-y-auto"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setAssignDialogOpen(false); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={
                !formAssetTypeId ||
                !formName ||
                createAsset.isPending ||
                updateAsset.isPending
              }
            >
              {editingAssetId
                ? updateAsset.isPending
                  ? 'Saving...'
                  : 'Save'
                : createAsset.isPending
                  ? 'Assigning...'
                  : 'Assign Asset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return Confirmation Dialog */}
      <Dialog open={!!returnDialogOpen} onOpenChange={(open) => { if (!open) setReturnDialogOpen(null); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Return Asset</DialogTitle>
            <DialogDescription>
              Confirm the return date for this asset.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-2">
            <Label htmlFor="return-date">Return Date</Label>
            <DatePicker
              id="return-date"
              value={returnDate}
              onChange={setReturnDate}
              placeholder="Select return date"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnDialogOpen(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleReturn}
              disabled={!returnDate || markReturned.isPending}
            >
              {markReturned.isPending ? 'Returning...' : 'Confirm Return'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteDialogOpen} onOpenChange={(open) => { if (!open) setDeleteDialogOpen(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Asset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this asset record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => deleteDialogOpen && handleDelete(deleteDialogOpen)}
              disabled={deleteAsset.isPending}
            >
              {deleteAsset.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
